import React, { useState } from 'react';
import { ArrowRight, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';

const FooterSection = () => {
  const navigate = useNavigate();
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  return (
    <>
      <footer className="bg-[#FAF9F6] border-t border-gray-200/50">
        <div className="max-w-[1800px] mx-auto px-6 lg:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {/* Connect */}
            <div>
              <h4 className="font-light tracking-wide mb-6 text-sm">Connect</h4>
              <ul className="space-y-3">
                <li>
                  <a href="#" className="text-gray-500 hover:text-black text-sm transition-colors font-light">Facebook</a>
                </li>
                <li>
                  <a href="#" className="text-gray-500 hover:text-black text-sm transition-colors font-light">Instagram</a>
                </li>
                <li>
                  <a href="mailto:contactus@bcstore.in" className="text-gray-500 hover:text-black text-sm transition-colors font-light">contactus@bcstore.in</a>
                </li>
              </ul>
            </div>

            {/* Terms and Policies */}
            <div>
              <h4 className="font-light tracking-wide mb-6 text-sm">Terms and Policies</h4>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => setShowPrivacyModal(true)} 
                    className="text-gray-500 hover:text-black text-sm transition-colors font-light"
                  >
                    Privacy Policy
                  </button>
                </li>
              </ul>
            </div>

            {/* Stay Inspired - Newsletter */}
            <div>
              <h4 className="font-light tracking-wide mb-6 text-sm">Stay Inspired</h4>
              <div className="flex max-w-md">
                <Input 
                  type="email" 
                  placeholder="Email address" 
                  className="flex-1 rounded-none border-0 border-b border-gray-300 bg-transparent focus:border-black focus-visible:ring-0 px-0 font-light placeholder:text-gray-400"
                />
                <button className="ml-4 text-black hover:opacity-70 transition-opacity">
                  <ArrowRight size={20} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="mt-16 pt-8 border-t border-gray-200/50 flex flex-col md:flex-row justify-between items-center">
            <p className="text-xs text-black mb-4 md:mb-0 font-medium">
              B&C Store
            </p>
            <p className="text-xs text-gray-400 font-light">
              © 2025 B&C Store
            </p>
            <p className="text-xs text-gray-400 font-light mt-2 md:mt-0">
              Developed by{' '}
              <a href="https://doutly.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-black transition-colors">
                Doutly
              </a>
              {' '}and{' '}
              <a href="https://venatriks.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-black transition-colors">
                Venatriks
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between">
              <h2 className="text-xl sm:text-2xl font-light tracking-wide">
                Terms and Policies
              </h2>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(85vh-80px)] text-sm text-gray-700 font-light space-y-6">
              <section>
                <h3 className="font-medium text-black mb-2">1. About Us</h3>
                <p>bcstore.in is an online store offering mobile accessories to customers in India.</p>
                <p className="mt-1">Contact Email: contactus@bcstore.in</p>
              </section>

              <section>
                <h3 className="font-medium text-black mb-2">2. Eligibility</h3>
                <p>By using this website, you confirm that:</p>
                <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                  <li>You are at least 18 years old or accessing under parental supervision.</li>
                  <li>You are legally capable of entering into a binding contract under Indian law.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black mb-2">3. Products and Services</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>We sell mobile accessories such as chargers, cables, cases, earphones, and related items.</li>
                  <li>Product images are for representation purposes only. Actual products may vary slightly.</li>
                  <li>Availability of products is subject to change without notice.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black mb-2">4. Pricing and Payment</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>All prices are listed in Indian Rupees (INR).</li>
                  <li>Prices may change without prior notice.</li>
                  <li>Payments must be completed through the payment methods available on the website.</li>
                  <li>We are not responsible for payment failures caused by third-party payment gateways.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black mb-2">5. Order Confirmation</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Once an order is placed, you will receive an order confirmation.</li>
                  <li>We reserve the right to cancel or refuse any order due to product unavailability, pricing errors, or suspicious activity.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black mb-2">6. Shipping Policy</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Orders are shipped within 2 days from the order date.</li>
                  <li>Delivery timelines may vary based on location, courier service, and unforeseen circumstances.</li>
                  <li>bcstore.in is not responsible for delays caused by courier partners, natural events, or government restrictions.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black mb-2">7. Return and Replacement Policy</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>We offer a 7-day return policy from the date of delivery.</li>
                  <li>Products must be unused, undamaged, and returned in original packaging.</li>
                  <li>Certain items may not be eligible for return due to hygiene or usage reasons.</li>
                  <li>Return requests must be initiated by contacting contactus@bcstore.in.</li>
                  <li>Refunds, if applicable, will be processed after inspection of the returned item.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black mb-2">8. Cancellation Policy</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Orders can be cancelled before shipping.</li>
                  <li>Once shipped, cancellation is not allowed and the return policy will apply.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black mb-2">9. User Responsibilities</h3>
                <p>You agree not to:</p>
                <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                  <li>Use the website for illegal or unauthorized purposes.</li>
                  <li>Provide false or misleading information.</li>
                  <li>Attempt to damage, hack, or disrupt the website or its services.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black mb-2">10. Intellectual Property</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>All content on bcstore.in including logos, images, text, and designs is the property of bcstore.in.</li>
                  <li>You may not copy, reproduce, or distribute any content without written permission.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black mb-2">11. Limitation of Liability</h3>
                <p>bcstore.in shall not be liable for:</p>
                <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                  <li>Indirect, incidental, or consequential damages.</li>
                  <li>Loss arising from misuse of products.</li>
                  <li>Website downtime or technical issues beyond our control.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-medium text-black mb-2">12. Changes to Terms</h3>
                <p>We reserve the right to update or modify these Terms at any time without prior notice. Continued use of the website constitutes acceptance of the revised Terms.</p>
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FooterSection;
